import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown'
import Button from '@mui/material/Button'
import ButtonGroup from '@mui/material/ButtonGroup'
import Chip from '@mui/material/Chip'
import DialogContent from '@mui/material/DialogContent'
import List from '@mui/material/List'
import Menu from '@mui/material/Menu'
import MenuItem from '@mui/material/MenuItem'
import Stack from '@mui/material/Stack'
import Tab from '@mui/material/Tab'
import Tabs from '@mui/material/Tabs'
import React, { useCallback, useState } from 'react'

import {
  useCreateLocationNoteMutation,
  useDeleteLocationNoteMutation,
  usePatchLocationNoteMutation,
  useUpdateLocationNoteMutation,
} from 'api/notes/locationNotesCrud'
import {
  useCreateWorkOrderNoteMutation,
  useDeleteWorkOrderNoteMutation,
  usePatchWorkOrderNoteMutation,
  useUpdateWorkOrderNoteNewMutation,
} from 'api/notes/workOrderNotesCrud'
import HHBaseDialog from 'components/common/HHBaseDialog'
import HHDialogActions from 'components/common/HHDialogActions'
import HHDialogTitle from 'components/common/HHDialogTitle'
import HHSectionPlaceholder from 'components/common/HHSectionPlaceholder'
import AddUpdateNoteDialog from 'components/notes/common/AddUpdateNoteDialog'
import NoteActionMenu from 'components/notes/common/NoteActionMenu'
import NoteListItem from 'components/notes/common/NoteListItem'
import AddUpdateCSNoteDialog from 'components/notes/configured-service/AddUpdateCSNoteDialog'
import CSNotes from 'components/notes/configured-service/CSNotes'
import CSNoteActionMenu from 'components/notes/configured-service/NoteActionMenu'
import LocationNotes from 'components/notes/location/LocationNotes'
import WONotes from 'components/notes/work-order/WONotes'
import { NOTE_TYPES } from 'settings/constants/note'
import T from 'T'

import useCombinedNotes from './useCombinedNotes'

import type { MergedNote } from './useCombinedNotes'

const HHBaseDialogUntyped = HHBaseDialog as React.ElementType
const HHSectionPlaceholderUntyped = HHSectionPlaceholder as React.ElementType
const AddUpdateNoteDialogUntyped = AddUpdateNoteDialog as React.ElementType
const AddUpdateCSNoteDialogUntyped = AddUpdateCSNoteDialog as React.ElementType
const NoteActionMenuUntyped = NoteActionMenu as React.ElementType
const CSNoteActionMenuUntyped = CSNoteActionMenu as React.ElementType
const NoteListItemUntyped = NoteListItem as React.ElementType
const LocationNotesUntyped = LocationNotes as React.ElementType
const CSNotesUntyped = CSNotes as React.ElementType

const { WORK_ORDER, LOCATION, CONFIGURED_SERVICE } = NOTE_TYPES as Record<string, string>

const TAB_ALL = 'all'

const ADD_MENU_ID = 'combined-notes-add-menu'
const ADD_MENU_BUTTON_ID = 'combined-notes-add-button'
const addMenuListProps = { 'aria-labelledby': ADD_MENU_BUTTON_ID }

const ADD_NOTE_LABELS: Record<string, string> = {
  [WORK_ORDER]: T.ADD_WORK_ORDER_NOTE,
  [LOCATION]: T.ADD_LOCATION_NOTE,
  [CONFIGURED_SERVICE]: T.ADD_SERVICE_NOTE,
}

/**
 * Combined Notes dialog (routes-board note-enhancement design): tabs for every note type an
 * entity carries — All / Service / Location / Work order — with pinned-first sorting, per-row
 * actions, and an "Add note" split menu reusing the existing per-type dialogs. Tabs render only
 * for the ids provided, so any surface (WO detail hero, route manager, ...) can mount it with
 * whatever subset it has.
 */
type CombinedNotesDialogProps = {
  open: boolean
  onClose: () => void
  workOrderId?: string
  locationId?: string
  configuredServiceId?: string
  // Which tab the dialog opens on — the surface decides: WO detail opens on its own
  // notes, route surfaces on the combined view. Falls back to All when the requested
  // tab's id wasn't passed, so nothing opens on a tab that doesn't render.
  defaultTab?: string
}

const resolveInitialTab = (defaultTab: string, workOrderId?: string, locationId?: string, configuredServiceId?: string) => {
  if (defaultTab === WORK_ORDER && workOrderId) return WORK_ORDER
  if (defaultTab === LOCATION && locationId) return LOCATION
  if (defaultTab === CONFIGURED_SERVICE && configuredServiceId) return CONFIGURED_SERVICE
  return TAB_ALL
}

const tabCountChipSx = { fontSize: 12, fontWeight: 600, height: 18, '& .MuiChip-label': { px: 0.75 } }

type NotesTabLabelProps = {
  label: string
  count: number
}

const NotesTabLabel: React.FC<NotesTabLabelProps> = ({ label, count }) => (
  <Stack alignItems="center" direction="row" gap={0.75}>
    <span>{label}</span>
    <Chip label={count} size="small" sx={tabCountChipSx} />
  </Stack>
)

// NoteListItem emits only the click event — this row closes over its note (same pattern as
// WONoteListItemRow) so the shared action menu and row-click edit know which note fired.
type CombinedNoteRowProps = {
  note: MergedNote
  showDivider: boolean
  onOpenActionMenu: (event: React.MouseEvent<HTMLElement>, note: MergedNote) => void
  onOpenEdit: (note: MergedNote) => void
}

const CombinedNoteRow: React.FC<CombinedNoteRowProps> = ({ note, showDivider, onOpenActionMenu, onOpenEdit }) => {
  const handleActionMenuClick = useCallback(
    (event: React.MouseEvent<HTMLElement>) => {
      event.stopPropagation()
      onOpenActionMenu(event, note)
    },
    [note, onOpenActionMenu]
  )
  const handleViewClick = useCallback(() => onOpenEdit(note), [note, onOpenEdit])

  return (
    <NoteListItemUntyped
      showSecondaryActions
      note={note}
      showDivider={showDivider}
      type={note.noteTypeTag}
      onActionMenuClick={handleActionMenuClick}
      onViewClick={handleViewClick}
    />
  )
}

const CombinedNotesDialog: React.FC<CombinedNotesDialogProps> = ({
  open,
  onClose,
  workOrderId,
  locationId,
  configuredServiceId,
  defaultTab = TAB_ALL,
}) => {
  const [activeTab, setActiveTab] = useState(() => resolveInitialTab(defaultTab, workOrderId, locationId, configuredServiceId))
  const [addMenuAnchorEl, setAddMenuAnchorEl] = useState<HTMLElement | null>(null)
  const [createType, setCreateType] = useState<string | null>(null)
  const [editNote, setEditNote] = useState<MergedNote | null>(null)
  const [actionMenuAnchorEl, setActionMenuAnchorEl] = useState<HTMLElement | null>(null)
  const [actionNote, setActionNote] = useState<MergedNote | null>(null)

  const { mergedNotes, woNotes, locationNotes, csNotes } = useCombinedNotes({
    configuredServiceId,
    enabled: open,
    locationId,
    workOrderId,
  })

  // The tab moves the default, never the availability — the caret stays reachable on every
  // tab, so a work order note is still one click away from the Location tab. All implies no
  // kind at all, so there the button opens the menu instead of picking one on the user's behalf.
  const primaryCreateType = activeTab === TAB_ALL ? '' : activeTab
  const isAddMenuOpen = Boolean(addMenuAnchorEl)

  const handleTabChange = useCallback((_event: React.SyntheticEvent, value: string) => setActiveTab(value), [])
  const handleOpenAddMenu = useCallback((event: React.MouseEvent<HTMLElement>) => setAddMenuAnchorEl(event.currentTarget), [])
  const handleCloseAddMenu = useCallback(() => setAddMenuAnchorEl(null), [])
  const handleCloseCreate = useCallback(() => setCreateType(null), [])
  const handleCloseEdit = useCallback(() => setEditNote(null), [])
  const handleCloseActionMenu = useCallback(() => setActionMenuAnchorEl(null), [])

  const handleAddWorkOrderNote = useCallback(() => {
    setAddMenuAnchorEl(null)
    setCreateType(WORK_ORDER)
  }, [])
  const handleAddLocationNote = useCallback(() => {
    setAddMenuAnchorEl(null)
    setCreateType(LOCATION)
  }, [])
  const handleAddServiceNote = useCallback(() => {
    setAddMenuAnchorEl(null)
    setCreateType(CONFIGURED_SERVICE)
  }, [])
  const handleAddPrimaryNote = useCallback(() => setCreateType(primaryCreateType), [primaryCreateType])

  const handleOpenActionMenu = useCallback((event: React.MouseEvent<HTMLElement>, note: MergedNote) => {
    event.stopPropagation()
    setActionNote(note)
    setActionMenuAnchorEl(event.currentTarget)
  }, [])

  const handleEditFromMenu = useCallback(() => {
    setActionMenuAnchorEl(null)
    setEditNote(actionNote)
  }, [actionNote])

  const handleOpenEdit = useCallback((note: MergedNote) => setEditNote(note), [])

  const actionType = actionNote?.noteTypeTag
  const editType = editNote?.noteTypeTag

  return (
    <>
      <HHBaseDialogUntyped fullWidth maxWidth="md" open={open} onClose={onClose}>
        <HHDialogTitle title={T.NOTES} onClose={onClose} />
        <Tabs sx={{ borderBottom: 1, borderColor: 'divider', px: 2 }} value={activeTab} onChange={handleTabChange}>
          <Tab label={<NotesTabLabel count={mergedNotes.length} label={T.ALL} />} value={TAB_ALL} />
          {configuredServiceId && <Tab label={<NotesTabLabel count={csNotes.length} label={T.SERVICE} />} value={CONFIGURED_SERVICE} />}
          {locationId && <Tab label={<NotesTabLabel count={locationNotes.length} label={T.LOCATION} />} value={LOCATION} />}
          {workOrderId && <Tab label={<NotesTabLabel count={woNotes.length} label={T.WORK_ORDER} />} value={WORK_ORDER} />}
        </Tabs>
        <DialogContent sx={{ minHeight: 320, p: 0 }}>
          {activeTab === TAB_ALL && mergedNotes.length === 0 && <HHSectionPlaceholderUntyped title={T.NO_NOTES_YET} />}
          {activeTab === TAB_ALL && mergedNotes.length > 0 && (
            <List disablePadding>
              {mergedNotes.map((note, index) => (
                <CombinedNoteRow
                  key={`${note.noteTypeTag}-${note.id}`}
                  note={note}
                  showDivider={index < mergedNotes.length - 1}
                  onOpenActionMenu={handleOpenActionMenu}
                  onOpenEdit={handleOpenEdit}
                />
              ))}
            </List>
          )}
          {/* CSNotes carries its own (stale) placeholder — render ours instead so the empty copy
              matches the other tabs without touching that legacy file. */}
          {activeTab === CONFIGURED_SERVICE && csNotes.length === 0 && <HHSectionPlaceholderUntyped title={T.NO_SERVICE_NOTES_YET} />}
          {activeTab === CONFIGURED_SERVICE && csNotes.length > 0 && (
            <CSNotesUntyped configuredServiceId={configuredServiceId} notes={csNotes} />
          )}
          {activeTab === LOCATION && (
            <LocationNotesUntyped locationId={locationId} noNotesText={T.NO_LOCATION_NOTES_YET} notes={locationNotes} />
          )}
          {activeTab === WORK_ORDER && <WONotes noNotesText={T.NO_WO_NOTES_YET} notes={woNotes} workOrderId={workOrderId} />}
        </DialogContent>
        <HHDialogActions>
          {primaryCreateType ? (
            <ButtonGroup variant="outlined">
              <Button onClick={handleAddPrimaryNote}>{ADD_NOTE_LABELS[primaryCreateType]}</Button>
              <Button
                aria-controls={isAddMenuOpen ? ADD_MENU_ID : undefined}
                aria-expanded={isAddMenuOpen}
                aria-haspopup="menu"
                aria-label={T.ADD_NOTE}
                id={ADD_MENU_BUTTON_ID}
                size="small"
                sx={{ px: 0 }}
                onClick={handleOpenAddMenu}
              >
                <ArrowDropDownIcon />
              </Button>
            </ButtonGroup>
          ) : (
            <Button
              aria-controls={isAddMenuOpen ? ADD_MENU_ID : undefined}
              aria-expanded={isAddMenuOpen}
              aria-haspopup="menu"
              endIcon={<ArrowDropDownIcon />}
              id={ADD_MENU_BUTTON_ID}
              variant="outlined"
              onClick={handleOpenAddMenu}
            >
              {T.ADD_NOTE}
            </Button>
          )}
          <Button variant="contained" onClick={onClose}>
            {T.CLOSE}
          </Button>
        </HHDialogActions>
      </HHBaseDialogUntyped>

      <Menu anchorEl={addMenuAnchorEl} id={ADD_MENU_ID} MenuListProps={addMenuListProps} open={isAddMenuOpen} onClose={handleCloseAddMenu}>
        {workOrderId && <MenuItem onClick={handleAddWorkOrderNote}>{T.WORK_ORDER}</MenuItem>}
        {locationId && <MenuItem onClick={handleAddLocationNote}>{T.LOCATION}</MenuItem>}
        {configuredServiceId && <MenuItem onClick={handleAddServiceNote}>{T.SERVICE}</MenuItem>}
      </Menu>

      {/* All-tab row actions: one shared menu instance per note type so each gets its own
          patch/delete hooks (CS reuses the shared menu — same {id, noteId, data} contract). */}
      <NoteActionMenuUntyped
        anchorEl={actionType === WORK_ORDER ? actionMenuAnchorEl : null}
        id={workOrderId}
        selectedNote={actionNote}
        useDeleteNoteMutation={useDeleteWorkOrderNoteMutation}
        usePatchNoteMutation={usePatchWorkOrderNoteMutation}
        onClose={handleCloseActionMenu}
        onEditClick={handleEditFromMenu}
      />
      <NoteActionMenuUntyped
        anchorEl={actionType === LOCATION ? actionMenuAnchorEl : null}
        id={locationId}
        selectedNote={actionNote}
        useDeleteNoteMutation={useDeleteLocationNoteMutation}
        usePatchNoteMutation={usePatchLocationNoteMutation}
        onClose={handleCloseActionMenu}
        onEditClick={handleEditFromMenu}
      />
      {/* CS rows use the specialized CS menu (mobile-visibility/archive submenus + POST-based
          delete) — same instance the Service tab's list uses. */}
      <CSNoteActionMenuUntyped
        anchorEl={actionType === CONFIGURED_SERVICE ? actionMenuAnchorEl : null}
        id={configuredServiceId}
        selectedNote={actionNote}
        onClose={handleCloseActionMenu}
        onEditClick={handleEditFromMenu}
      />

      {(createType === WORK_ORDER || editType === WORK_ORDER) && (
        <AddUpdateNoteDialogUntyped
          isOpen
          existingNote={editType === WORK_ORDER ? editNote : undefined}
          id={workOrderId}
          title={editType === WORK_ORDER ? T.WORK_ORDER_NOTE : T.NEW_WORK_ORDER_NOTE}
          useCreateNoteMutation={useCreateWorkOrderNoteMutation}
          useUpdateNoteMutation={useUpdateWorkOrderNoteNewMutation}
          onClose={editType === WORK_ORDER ? handleCloseEdit : handleCloseCreate}
        />
      )}
      {(createType === LOCATION || editType === LOCATION) && (
        <AddUpdateNoteDialogUntyped
          isOpen
          existingNote={editType === LOCATION ? editNote : undefined}
          id={locationId}
          title={editType === LOCATION ? T.LOCATION_NOTE : T.NEW_LOCATION_NOTE}
          useCreateNoteMutation={useCreateLocationNoteMutation}
          useUpdateNoteMutation={useUpdateLocationNoteMutation}
          onClose={editType === LOCATION ? handleCloseEdit : handleCloseCreate}
        />
      )}
      {(createType === CONFIGURED_SERVICE || editType === CONFIGURED_SERVICE) && (
        <AddUpdateCSNoteDialogUntyped
          isOpen
          existingNote={editType === CONFIGURED_SERVICE ? editNote : undefined}
          id={configuredServiceId}
          title={editType === CONFIGURED_SERVICE ? T.CONFIGURED_SERVICE_NOTE : T.NEW_CONFIGURED_SERVICE_NOTE}
          onClose={editType === CONFIGURED_SERVICE ? handleCloseEdit : handleCloseCreate}
        />
      )}
    </>
  )
}

export default CombinedNotesDialog
